/**
 * @rtvloco/theme — ThemeManager.ts
 *
 * Single source of truth for theme state across all SMARTTT apps.
 *
 * ── THE CONTRACT ─────────────────────────────────────────────────────────
 * There is exactly ONE decision point for "what theme is this page in?":
 * this module. Every surface — page root, shell/nav chrome, individual
 * component, native form control — inherits that single decision. Nothing
 * downstream may make its own theme decision:
 *
 *   1. No component/page may call `setAttribute('data-theme', …)`,
 *      set `className="dark"`, or otherwise stamp a theme onto any element.
 *      `data-theme` is a plain (non `:root`-scoped) attribute selector in
 *      light.css/dark.css, so an element that sets it locally creates a
 *      "theme island" that disagrees with the rest of the page — this is
 *      exactly the "some pages go full dark" / "light page, dark nav" bug
 *      class. Only `_apply()` below may set it, and only on
 *      `document.documentElement`.
 *   2. No component may read `window.matchMedia('(prefers-color-scheme...)')`
 *      or use a raw CSS `@media (prefers-color-scheme: dark)` block. Only
 *      this module resolves the `'system'` preference. Components that need
 *      dark-mode-specific styling key off `[data-theme='dark']` instead,
 *      which reflects the user's *resolved, explicit* theme — not the OS
 *      setting underneath a theme the user may have overridden.
 *   3. No component may set the CSS `color-scheme` property (inline style or
 *      stylesheet). `_apply()` sets it once, on `document.documentElement`,
 *      so every native control (date/time pickers, `<select>`, scrollbars,
 *      checkboxes) renders with the same chrome as the rest of the page.
 *   4. Apps may declare a per-app default (see `defaultPref` below) for the
 *      *unset-preference* case, but must do so only through
 *      `initialize({ defaultPref })` — never by writing `localStorage`
 *      directly. The `index.html` flash-prevention inline script (see each
 *      app's `index.html`) is the one unavoidable, documented exception: it
 *      must run synchronously before any JS module loads, so it duplicates
 *      just the resolution algorithm and the default literal. A regression
 *      test in each app asserts that literal matches the `defaultPref`
 *      passed to `initialize()`, so the two can't drift.
 *
 * `ui/occ-client/src/__tests__/themeSingleSource.test.ts` statically scans
 * the app for violations of rules 1–3.
 *
 * Design rules:
 * - initialize() MUST be called before the React root renders.
 * - setTheme() updates DOM, tokens, localStorage, and notifies all listeners
 *   in one synchronous pass — no race conditions.
 * - MapLibre hooks listen to THEME_CHANGE_EVENT on document and call
 *   setPaintProperty — no layer rebuilds required.
 * - Works in SSR / Node (all DOM/localStorage accesses are guarded).
 */
import { getThemeTokens } from './tokens';
// ── Public event ──────────────────────────────────────────────────────────
/** Custom DOM event name fired after every theme change. */
export const THEME_CHANGE_EVENT = 'smarttt:themechange';
// ── localStorage key (shared with index.html flash-prevention script) ─────
const STORAGE_KEY = 'smarttt-theme';
// ── Manager class ─────────────────────────────────────────────────────────
class ThemeManagerImpl {
    _theme = 'light';
    _tokens = getThemeTokens('light');
    _listeners = new Set();
    _mql = null;
    _mqlHandler = null;
    _initialized = false;
    /**
     * Initialize the manager from persisted state.
     * Must be called synchronously before the React root renders so that the
     * first paint uses the correct theme without a flash.
     *
     * Safe to call multiple times — subsequent calls are no-ops.
     *
     * @param options.defaultPref theme to use when no preference was ever
     *   persisted. Defaults to `'system'`. See {@link ThemeManagerInitOptions}.
     */
    initialize(options) {
        if (this._initialized)
            return;
        this._initialized = true;
        const stored = typeof localStorage !== 'undefined'
            ? localStorage.getItem(STORAGE_KEY)
            : null;
        this._apply(stored ?? options?.defaultPref ?? 'system');
    }
    /** Current resolved theme ('light' | 'dark'). */
    getTheme() {
        return this._theme;
    }
    /** Current resolved token table. All values are concrete hex/rgba/numbers. */
    getTokens() {
        return this._tokens;
    }
    /**
     * Set the active theme.
     *
     * @param pref 'light' | 'dark' | 'system'
     *   'system' resolves via prefers-color-scheme and re-resolves on OS change.
     *
     * Synchronously:
     *   1. Writes to localStorage.
     *   2. Resolves to 'light' or 'dark'.
     *   3. Sets data-theme on document.documentElement.
     *   4. Updates internal token table.
     *   5. Dispatches THEME_CHANGE_EVENT on document.
     *   6. Calls all subscribe() listeners.
     */
    setTheme(pref) {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, pref);
        }
        this._apply(pref);
    }
    /**
     * Subscribe to theme changes.
     * The listener is called immediately with the current theme if the manager
     * is already initialized, then on every subsequent change.
     *
     * @returns Unsubscribe function — call it to remove the listener.
     */
    subscribe(listener) {
        this._listeners.add(listener);
        if (this._initialized) {
            listener(this._theme, this._tokens);
        }
        return () => this._listeners.delete(listener);
    }
    // ── Private ───────────────────────────────────────────────────────────────
    _resolve(pref) {
        if (pref !== 'system')
            return pref;
        return typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }
    _apply(pref) {
        // Tear down previous matchMedia listener (only active in 'system' mode).
        if (this._mql && this._mqlHandler) {
            this._mql.removeEventListener('change', this._mqlHandler);
            this._mql = null;
            this._mqlHandler = null;
        }
        const resolved = this._resolve(pref);
        this._theme = resolved;
        this._tokens = getThemeTokens(resolved);
        // 1. Apply data-theme attribute so CSS variables resolve correctly.
        //    This is the ONLY place in any app that may set data-theme — see the
        //    contract note at the top of this file.
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', resolved);
            // 1a. Native control chrome (date/time pickers, <select>, scrollbars,
            //     checkboxes) is driven by the CSS `color-scheme` property, which
            //     is independent of our custom-property tokens. Set it once, here,
            //     so no component ever needs (or is allowed) to set it itself.
            document.documentElement.style.colorScheme = resolved;
            // 1b. Browser/OS chrome (mobile address bar, PWA task-switcher card,
            //     Android system nav bar) reads <meta name="theme-color">. Keep it
            //     in sync with the resolved surface colour so that chrome never
            //     shows dark on top of a light page or vice-versa. Apps that don't
            //     ship this meta tag are unaffected (querySelector returns null).
            const themeColorMeta = document.querySelector('meta[name="theme-color"]');
            if (themeColorMeta) {
                themeColorMeta.setAttribute('content', this._tokens.colorBg);
            }
        }
        // 2. Dispatch DOM event — MapLibre hooks and non-React consumers listen here.
        if (typeof document !== 'undefined') {
            const detail = { theme: resolved, tokens: this._tokens };
            document.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, {
                detail,
                bubbles: false,
            }));
        }
        // 3. Notify React subscribers (useThemeTokens hook).
        this._listeners.forEach((fn) => fn(resolved, this._tokens));
        // 4. Re-register system preference listener if needed.
        if (pref === 'system' && typeof window !== 'undefined') {
            this._mql = window.matchMedia('(prefers-color-scheme: dark)');
            this._mqlHandler = () => this._apply('system');
            this._mql.addEventListener('change', this._mqlHandler);
        }
    }
}
// ── Singleton export ───────────────────────────────────────────────────────
/**
 * Global ThemeManager instance.
 *
 * Usage in app bootstrap (before React render):
 *   import { themeManager } from '@rtvloco/theme/ThemeManager';
 *   themeManager.initialize();
 *
 * Usage in React hook:
 *   const unsub = themeManager.subscribe((theme, tokens) => setState(tokens));
 *
 * Usage in MapLibre hook:
 *   document.addEventListener(THEME_CHANGE_EVENT, (e) => {
 *     const { tokens } = (e as CustomEvent<ThemeChangeDetail>).detail;
 *     map.setPaintProperty('vehicle-base-layer-dot', 'circle-color',
 *       buildDeviationExpression(tokens));
 *   });
 */
export const themeManager = new ThemeManagerImpl();
//# sourceMappingURL=ThemeManager.js.map